import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  OutlinedInput,
  Select,
  MenuItem,
  Pagination,
  Button,
  CardMedia,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { size, COLORS } from "../../utils";
import SearchIcon from "../../assets/icons/search";
import { makeStyles } from "@mui/styles";
import Enquire from "./Enquire";
import ProjectsList from "../../assets/extras/ProjectsList.json";

const useOutlinedInputStyles = makeStyles(() => ({
  root: {
    "& $notchedOutline": {
      border: `${size({ p: 0.1 })} solid ${COLORS.C1}`,
    },
    "&:hover $notchedOutline": {
      border: `${size({ p: 0.1 })} solid ${COLORS.C1}`,
    },
    "&$focused $notchedOutline": {
      border: `${size({ p: 0.1 })} solid ${COLORS.C1}`,
    },
  },
  focused: {
    border: `${size({ p: 0.1 })} solid ${COLORS.C1}`,
  },
  notchedOutline: {
    border: `${size({ p: 0.1 })} solid ${COLORS.C1}`,
  },
}));

export default function Project() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [domain, setDomain] = useState("All");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);

  const [projectsList,] = useState(ProjectsList)

  const ois = useOutlinedInputStyles();

  // Extract unique domains from ProjectsList and add "All"
  const domains = [
    "All",
    ...Array.from(new Set(ProjectsList.map((p) => p.Domain))),
  ];

  // Filtered list based on domain and search (search in Title OR Domain)
  const filteredProjects = ProjectsList.filter((project) => {
    const matchDomain =
      domain === "All" ||
      (project.Domain && project.Domain.toLowerCase() === domain.toLowerCase());

    const matchSearch =
      search.length < 1 ||
      (project.Title &&
        project.Title.toLowerCase().includes(search.toLowerCase())) ||
      (project.Domain &&
        project.Domain.toLowerCase().includes(search.toLowerCase()));

    return matchDomain && matchSearch;
  });

  // Pagination config
  const itemsPerPage = 10;
  const pageCount = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  function changeDomain(e) {
    setDomain(e.target.value);
    setPage(1); // Reset page to 1 when domain changes
  }

  function searchUpdate(e) {
    setSearch(e.target.value);
    setPage(1); // Reset page to 1 when search changes
  }

  const PROCESS_DATA = [
    {
      icon: "/assets/images/projects/selection.png",
      name: "Title Selection",
      desc: "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
    },
    {
      icon: "/assets/images/projects/implementation.png",
      name: "Implementation",
      desc: "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
    },
    {
      icon: "/assets/images/projects/explain.png",
      name: "Explanation",
      desc: "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
    },
    {
      icon: "/assets/images/projects/update.png",
      name: "Updation",
      desc: "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
    },
    {
      icon: "/assets/images/projects/document.png",
      name: "Documentation + PPT",
      desc: "Lorem Ipsum is simply dummy text of the printing and typesetting industry.",
    },
  ];

  return (
    <Grid
      item
      container
      flexWrap={"nowrap"}
      direction={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      rowGap={size({ p: 5, h: true })}
    >
      <Grid
        item
        container
        flexWrap={"nowrap"}
        direction={"column"}
        alignItems={"center"}
        rowGap={size({ p: 0.5, h: true })}
      >
        <Grid item>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: size({ p: 2 }),
              fontWeight: 600,
              color: COLORS.C2,
              ...(isMobile && {
                fontSize: size({ p: 4 }),
              }),
            }}
          >
            Genesys Academy Research Projects (GARP)
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            sx={{
              fontFamily: "Space Mono",
              fontSize: size({ p: 0.9 }),
              fontWeight: 200,
              color: COLORS.C2,
              ...(isMobile && {
                fontSize: size({ p: 2.7 }),
                px: size({ p: 10 }),
                textAlign: "center",
                marginTop: "5px",
              }),
            }}
          >
            Welcome — the trusted destination for innovative project solutions.
          </Typography>
        </Grid>
      </Grid>
      <Grid
        item
        container
        flexWrap={"nowrap"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Grid
          item
          container
          flexWrap={"nowrap"}
          alignItems={"center"}
          width={size({ p: 49 })}
          columnGap={size({ p: 1 })}
        >
          <Grid item>
            <Typography
              sx={{
                fontFamily: "Space Mono",
                fontSize: size({ p: 1.2 }),
                fontWeight: 600,
                color: COLORS.C2,
                ...(isMobile && {
                  fontSize: size({ p: 2.5 }),
                }),
              }}
            >
              Domain :
            </Typography>
          </Grid>
          <Grid item width={size({ p: 18 })}>
            <Select
              fullWidth
              value={domain}
              onChange={changeDomain}
              input={<OutlinedInput classes={ois} />}
              sx={{
                fontFamily: "Space Mono",
                color: COLORS.C2,
                border: `none`,
                borderRadius: 0,
                ...(isMobile && {
                  height: size({ p: 6.5 }),
                }),
              }}
            >
              {domains.map((dom, index) => (
                <MenuItem
                  key={index}
                  value={dom}
                  sx={{
                    fontFamily: "Space Mono",
                    color: COLORS.C2,
                    backgroundColor: COLORS.WHITE,
                  }}
                >
                  {dom}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid
          item
          width={size({ p: 18 })}
          sx={{
            border: `${size({ p: 0.08 })} solid ${COLORS.C2}`,
            ...(isMobile && {
              width: size({ p: 25 }),
            }),
          }}
        >
          <TextField
            placeholder={"Search"}
            fullWidth
            value={search}
            onChange={searchUpdate}
            InputProps={{
              style: {
                fontFamily: "Space Mono",
                color: COLORS.C2,
                fontSize: size({ p: 1 }),
                height: size({ p: 2.5 }),
                ...(isMobile && {
                  height: size({ p: 6 }),
                }),
              },
            }}
            sx={{
              "& fieldset": { border: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  border: `${size({ p: 0.08 })} solid ${COLORS.C2}`,
                  borderRadius: 0,
                },
              },
            }}
          />
        </Grid>
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: COLORS.C2,
            padding: size({ p: 0.33 }),
            ...(isMobile && {
              padding: size({ p: 2.1 }),
            }),
          }}
        >
          <SearchIcon
            fill={COLORS.WHITE}
            width={isMobile ? size({ p: 2.3 }) : size({ p: 2 })}
            height={isMobile ? size({ p: 2.3 }) : size({ p: 2 })}
          />
        </Grid>
      </Grid>
      <Grid
        item
        container
        flexWrap={"nowrap"}
        direction={"column"}
        alignItems={"center"}
        width={size({ p: 70 })}
        rowGap={size({ p: 1, h: true })}
      >
        <Grid
          item
          container
          flexWrap={"nowrap"}
          width={size({ p: 70 })}
          alignItems={"center"}
          columnGap={size({ p: 1 })}
          padding={`0px ${size({ p: 2 })}`}
          height={isMobile ? size({ p: 7, h: true }) : size({ p: 10, h: true })}
          sx={{
            backgroundColor: COLORS.C2,
            color: "white",
            border: `${size({ p: 0.08 })} solid ${COLORS.BLACK}A0`,
            // borderRight: isMobile?`${size({ p: 0.65 })} solid ${COLORS.BLACK}A0`:`${size({ p: 0.25 })} solid ${COLORS.BLACK}A0`,
            // borderBottom: isMobile?`${size({ p: 0.65 })} solid ${COLORS.BLACK}A0`:`${size({ p: 0.25 })} solid ${COLORS.BLACK}A0`,
            // borderTop: `${size({ p: 0.25 })} solid ${COLORS.BLACK}A0`,
            // borderLeft: `${size({ p: 0.25 })} solid ${COLORS.BLACK}A0`,
            ...(isMobile && {
              width: size({ p: 77 }),
            }),
          }}
        >
          <Grid
            item
            width={size({ p: 6 })}
            minWidth={size({ p: 6 })}
            sx={{
              ...(isMobile && {
                width: size({ p: 7 }),
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: "Space Mono",
                fontSize: size({ p: 1.3 }),
                fontWeight: 600,
                color: COLORS.WHITE,
                textAlign: "center",
                ...(isMobile && {
                  fontSize: size({ p: 2.5 }),
                }),
              }}
            >
              CODE
            </Typography>
          </Grid>

          <Grid
            item
            width={size({ p: 46 })}
            minWidth={size({ p: 6 })}
            textAlign={"center"}
            sx={{
              ...(isMobile && {
                width: size({ p: 47 }),
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: "Space Mono",
                fontSize: size({ p: 1.3 }),
                fontWeight: 600,
                color: COLORS.WHITE,
                ...(isMobile && {
                  fontSize: size({ p: 2.5 }),
                }),
              }}
            >
              PROJECT TITLE
            </Typography>
          </Grid>

          <Grid
            item
            width={size({ p: 14.08 })}
            minWidth={size({ p: 6 })}
            textAlign={"center"}
            sx={{
              ...(isMobile && {
                width: size({ p: 15 }),
              }),
            }}
          >
            <Typography
              sx={{
                fontFamily: "Space Mono",
                fontSize: size({ p: 1.3 }),
                fontWeight: 600,
                color: COLORS.WHITE,
                ...(isMobile && {
                  fontSize: size({ p: 2.5 }),
                }),
              }}
            >
              DOMAIN
            </Typography>
          </Grid>
        </Grid>
        {paginatedProjects.map((k) => (
          <Grid
            key={k.Code}
            item
            container
            flexWrap={"nowrap"}
            width={size({ p: 70 })}
            alignItems={"center"}
            columnGap={size({ p: 1 })}
            padding={`0px ${size({ p: 2 })}`}
            height={size({ p: 12, h: true })}
            sx={{
              backgroundColor: COLORS.WHITE,
              border: `${size({ p: 0.08 })} solid ${COLORS.C2}A0`,
              borderRight: `${size({ p: 0.25 })} solid ${COLORS.C2}A0`,
              ...(isMobile && {
                width: size({ p: 77 }),
              }),
            }}
          >
            <Grid
              item
              width={size({ p: 6 })}
              minWidth={size({ p: 6 })}
              sx={{
                ...(isMobile && {
                  width: size({ p: 7 }),
                }),
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: size({ p: 1 }),
                  fontWeight: 600,
                  color: COLORS.C2,
                  ...(isMobile && {
                    fontSize: size({ p: 2 }),
                  }),
                }}
              >
                {k.Code}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                width: "1px",
                minWidth: "2px",
                maxWidth: "2px",
                height: size({ p: 10, h: true }),
                backgroundColor: COLORS.C2 + "A0",
              }}
            ></Grid>
            <Grid item width={size({ p: 46 })} minWidth={size({ p: 46 })}>
              <Typography
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: size({ p: 1 }),
                  fontWeight: 400,
                  color: COLORS.C2,
                  ...(isMobile && {
                    fontSize: size({ p: 2 }),
                  }),
                }}
              >
                {k.Title}
              </Typography>
            </Grid>
            <Grid
              item
              sx={{
                width: "1px",
                minWidth: "2px",
                maxWidth: "2px",
                height: size({ p: 10, h: true }),
                backgroundColor: COLORS.C2 + "A0",
              }}
            ></Grid>
            <Grid item>
              <Typography
                sx={{
                  fontFamily: "Space Mono",
                  fontSize: size({ p: 1 }),
                  fontWeight: 600,
                  color: COLORS.C2,
                  ...(isMobile && {
                    fontSize: size({ p: 1.8 }),
                  }),
                }}
              >
                {k.Domain}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Grid>
      {filteredProjects.length > itemsPerPage && (
        <Grid item>
          <Pagination
            count={pageCount}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={(_, page) => setPage(page)}
            sx={{
              ".MuiPaginationItem-root": {
                backgroundColor: COLORS.C3,
                fontFamily: "Space Mono",
                color: COLORS.C8,
                margin: `0px ${size({ p: 0.5 })}`,
                "&.Mui-selected": {
                  backgroundColor: COLORS.C2,
                  color: COLORS.WHITE,
                },
                "&:hover": {
                  backgroundColor: COLORS.C2 + "A0",
                  color: COLORS.WHITE,
                },
              },
            }}
          />
        </Grid>
      )}
      <Grid
        item
        container
        flexWrap={"nowrap"}
        direction={"column"}
        width={size({ p: 80 })}
        alignItems={"center"}
        justifyContent={"center"}
        rowGap={size({ p: 0.5, h: true })}
        height={isMobile ? size({ p: 50, h: true }) : size({ p: 60, h: true })}
        sx={{
          background: `radial-gradient(57.03% 76.98% at 100% 0%,#ffffff 0%,rgba(255,255,255,0) 100%),#031c26`,
          backgroundBlendMode: "overlay,normal",
          borderRadius: size({ p: 1.5 }),
          marginTop: size({ p: 10, h: true }),
        }}
      >
        <Grid item>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: size({ p: 2 }),
              fontWeight: 600,
              color: COLORS.WHITE,
              ...(isMobile && {
                fontSize: size({ p: 4 }),
              }),
            }}
          >
            What's included in project_
          </Typography>
        </Grid>
        <Grid item>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: size({ p: 1 }),
              fontWeight: 400,
              textAlign: "center",
              color: `${COLORS.WHITE}`,
              padding: `0px ${size({ p: 15 })}`,
              ...(isMobile && {
                fontSize: size({ p: 2 }),
                px: size({ p: 6 }),
                marginTop: "1px",
              }),
            }}
          >
            From precise title selection and detailed explanations to regular
            updates and meticulous documentation, we cover every facet to ensure
            your project's success.
          </Typography>
        </Grid>
        <Grid
          item
          container
          flexWrap={"nowrap"}
          alignItems={"center"}
          justifyContent={"center"}
          marginTop={size({ p: 6, h: true })}
          marginBottom={size({ p: 6, h: true })}
        >
          {PROCESS_DATA.map((k) => {
            return (
              <Grid
                key={k.name}
                item
                container
                flexWrap={"nowrap"}
                direction={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                width={size({ p: 15 })}
              >
                <Grid item>
                  <CardMedia
                    component={"img"}
                    image={k.icon}
                    sx={{
                      width: size({ p: 7 }),
                      height: size({ p: 7 }),
                      ...(isMobile && {
                        width: size({ p: 10 }),
                        height: size({ p: 10 }),
                      }),
                    }}
                  />
                </Grid>
                <Grid item>
                  <Typography
                    sx={{
                      fontFamily: "Space Mono",
                      fontSize: size({ p: 1.2 }),
                      fontWeight: 600,
                      color: COLORS.WHITE,
                      marginTop: size({ p: 2, h: true }),
                      ...(isMobile && {
                        fontSize: size({ p: 1.9 }),
                        textAlign: "center",
                      }),
                    }}
                  >
                    {k.name}
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
        <Grid item width={size({ p: 10 })}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => {
              setOpen(true);
            }}
            sx={{
              backgroundColor: `${COLORS.C3}`,
              fontFamily: "Space Mono",
              fontWeight: 400,
              fontSize: size({ p: 1 }),
              color: COLORS.BLACK,
              textTransform: "none",
              ":hover": {
                backgroundColor: `${COLORS.C3}`,
              },
              ...(isMobile && {
                fontSize: size({ p: 2 }),
              }),
            }}
          >
            Enquire Now
          </Button>
        </Grid>
      </Grid>
      <Enquire open={open} setOpen={setOpen} data={projectsList}/>
    </Grid>
  );
}
